import { useMemo } from "react";
import { IconSearch } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  Badge, Card, EmptyState, Group, Input, PageHeader, PaginadorTabla, Stack, TablaSkeleton, Table, Tbody, Td, Text, Th, Thead, Tr,
} from "@/components/ui";
import { useServerPagination } from "@/lib/hooks/use-server-pagination";
import { useBandejaRevision } from "../hooks/use-revision-documental";
import { EstadoRevisionBadge } from "./EstadoRevisionBadge";
import { VigenciaBadge } from "./VigenciaBadge";

interface Filtros {
  busqueda: string;
}

/**
 * Bandeja «Revisión documental» (Gestión Documental · spec §10): documentos pendientes de
 * revisión que le tocan al usuario por los roles revisores configurados en su categoría o tipo
 * (o todos, con `gestion_documental.revisar_todo`). Nunca aparecen los de solicitudes que el
 * propio usuario capturó (H-4). La revisión se hace en el expediente de la solicitud.
 */
export function BandejaRevisionDocumentalPage() {
  const p = useServerPagination<Filtros>({ busqueda: "" });
  const params = useMemo(
    () => ({
      ...(p.filtrosDebounced.busqueda.trim() ? { busqueda: p.filtrosDebounced.busqueda.trim() } : {}),
      page: p.page,
      pageSize: p.pageSize,
    }),
    [p.filtrosDebounced, p.page, p.pageSize]
  );
  const { data } = useBandejaRevision(params);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  if (data && !data.puedeRevisar) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <EmptyState title="Acceso restringido" description="Tu usuario no revisa documentos de ninguna categoría." />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <PageHeader
        title="Revisión documental"
        description="Documentos de las solicitudes de pago que esperan tu revisión antes de pasar a autorización."
        breadcrumbs={[{ label: "Inicio", href: "/dashboard" }, { label: "Revisión documental" }]}
      />
      <Stack gap="md">
        <Group justify="space-between" className="flex-wrap">
          <Input
            placeholder="Buscar por folio, documento o tipo…"
            leftSection={<IconSearch size={14} stroke={1.7} />}
            value={p.filtros.busqueda}
            onChange={(e) => p.patchFiltros({ busqueda: e.currentTarget.value })}
            size="xs"
            wrapperClassName="w-70"
          />
          <Text size="sm" c="dimmed">
            {data ? (total === 0 ? "Sin documentos por revisar." : `${total} documento${total === 1 ? "" : "s"} por revisar.`) : null}
          </Text>
        </Group>
        <PaginadorTabla cargando={!data} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} variante="superior" />
        <Card padding="lg">
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm" minWidth={820}>
              <Thead>
                <Tr>
                  <Th>Solicitud</Th>
                  <Th>Documento</Th>
                  <Th>Versión</Th>
                  <Th>Cargado</Th>
                  <Th>Estado</Th>
                  <Th align="center">Acciones</Th>
                </Tr>
              </Thead>
              {!data ? (
                <TablaSkeleton dentroDeTabla columnas={[{}, { lineas: 2 }, {}, {}, {}, { align: "center" }]} filas={p.pageSize} />
              ) : (
                <Tbody>
                  {items.length === 0 ? (
                    <Tr>
                      <Td colSpan={6}>
                        <Text size="sm" c="dimmed" ta="center" className="py-5">
                          No hay documentos por revisar.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    items.map((d) => (
                      <Tr key={d.documentoId}>
                        <Td>
                          <Text size="sm" fw={600}>
                            {d.folio}
                          </Text>
                          {d.subtipo ? (
                            <Text size="xs" c="dimmed">
                              {d.subtipo}
                            </Text>
                          ) : null}
                        </Td>
                        <Td>
                          <Text size="sm">
                            {d.categoriaNombre}
                            {d.tipoDocumento ? ` · ${d.tipoDocumento}` : ""}
                          </Text>
                          <Text size="xs" c="dimmed" truncate title={d.nombreOriginal}>
                            {d.nombreOriginal}
                          </Text>
                        </Td>
                        <Td>
                          <Badge size="xs" color="slate">
                            v{d.versionActual}
                          </Badge>
                        </Td>
                        <Td>{dayjs(d.cargadoAt).format("DD/MM/YYYY HH:mm")}</Td>
                        <Td>
                          <div className="flex flex-wrap gap-1">
                            <EstadoRevisionBadge estado={d.estadoRevision} accion={d.accionObservacion} />
                            <VigenciaBadge fechaVencimiento={d.fechaVencimiento} vencido={d.vencido} />
                          </div>
                        </Td>
                        <Td align="center">
                          <Link
                            to="/solicitudes/$id/expediente"
                            params={{ id: d.solicitudId }}
                            className="text-sm font-medium text-multiva-600 hover:underline"
                          >
                            Revisar
                          </Link>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              )}
            </Table>
          </div>
        </Card>
        <PaginadorTabla cargando={!data} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} sustantivo="documentos" />
      </Stack>
    </div>
  );
}
